import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TextField, Button, Typography, Snackbar, Alert, MenuItem } from "@mui/material";
import { addDoc, collection, doc, setDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getSubjectsForTutorType } from "../constants";
import {
  STRIPE_CONNECT_DOC_ID,
  STRIPE_CONNECT_SUBCOLLECTION,
  STRIPE_TEACHER_DEFAULTS,
} from "../utils/stripeTeacherDefaults";
import Select from 'react-select';
import makeAnimated from 'react-select/animated';

// ValidationUtils.js (extrait directement intégré ici pour le moment)
const isValidEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

const animatedComponents = makeAnimated();

const NewTeacherForm = ({ onClose, setTeachers, teachers }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(''); 
  const [disabledButton, setDisabledButton] = useState(false);
  const [teacherData, setTeacherData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    uid: "",
    tutorType: "Teacher",
  });

  // Séparer subjectIds de teacherData
  const [subjectIds, setSubjectIds] = useState([]);

  // Nouvelle état pour l'inputValue
  const [selectInputValue, setSelectInputValue] = useState("");

  // États pour les Snackbars
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" ou "error"

  // Mémoriser les options de matières
  const subjectOptions = useMemo(() => {
    return getSubjectsForTutorType(teacherData.tutorType).map((subject) => ({
      value: subject.id,
      label: `${subject.name} (${subject.level})`,
    }));
  }, [teacherData.tutorType]);

  // Mémoriser la valeur sélectionnée
  const selectedSubjects = useMemo(() => {
    return subjectOptions.filter((option) => subjectIds.includes(option.value));
  }, [subjectIds, subjectOptions]);

  // Utiliser useCallback pour mémoriser handleSubjectChange
  const handleSubjectChange = useCallback((selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map((option) => option.value) : [];
    setSubjectIds(selectedIds);
  }, []);

  const handleTutorTypeChange = (event) => {
    setTeacherData({ ...teacherData, tutorType: event.target.value });
    setSubjectIds([]);
  };

  // Handler pour l'input de recherche
  const handleInputChange = useCallback((newValue, { action }) => {
    if (action === 'input-change' || action === 'menu-open') {
      setSelectInputValue(newValue);
    }
  }, []);

  // Fonction de validation des données avant la soumission
  const validateTeacherData = () => {
    if (!teacherData.firstName.trim()) {
      setSnackbarMessage(t("First name is required"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    if (!teacherData.lastName.trim()) {
      setSnackbarMessage(t("Last name is required"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    if (!teacherData.email.trim()) {
      setSnackbarMessage(t("Email is required"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    if (!isValidEmail(teacherData.email)) {
      setSnackbarMessage(t("Please enter a valid email"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    if (teacherData.email !== teacherData.email.toLowerCase()) {
      setSnackbarMessage(t("Email must contain only lowercase letters"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    if (!teacherData.phone.trim()) {
      setSnackbarMessage(t("Phone number is required"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    if (!teachers || !Array.isArray(teachers)) {
      console.error("Teachers list is missing or invalid");
      setSnackbarMessage(t("Internal error"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    if (!teacherData.tutorType) {
      setSnackbarMessage(t("Tutor type is required"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    if ((subjectIds || []).length === 0) {
      setSnackbarMessage(t("You must choose at least one subject"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    // Duplicate email check
    const isDuplicateEmail = teachers.some(
      (teacher) => (teacher.email || "").toLowerCase() === (teacherData.email || "").toLowerCase()
    );
    if (isDuplicateEmail) {
      setSnackbarMessage(t("A teacher with the same email already exists"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    // Duplicate full name check
    const isDuplicateName = teachers.some(
      (teacher) =>
        (teacher.firstName || "").toLowerCase() === (teacherData.firstName || "").toLowerCase() &&
        (teacher.lastName || "").toLowerCase() === (teacherData.lastName || "").toLowerCase()
    );
    if (isDuplicateName) {
      setSnackbarMessage(t("A teacher with the same first and last name already exists"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      return false;
    }

    return true;
  };

  const handleAddTeacher = async () => {
    if (!validateTeacherData()) return;
    setDisabledButton(true)
    try {
      const newTeacher = {
        ...teacherData,
        tutorType: teacherData.tutorType || "Teacher",
        subjectIds: subjectIds,
        groupIds: [],
        fullyRegistered: false,
        role: "teacher",
      };

      // Ajouter dans Firestore
      const docRef = await addDoc(collection(db, "teachers"), newTeacher);
      const stripeConnectRef = doc(
        db,
        "teachers",
        docRef.id,
        STRIPE_CONNECT_SUBCOLLECTION,
        STRIPE_CONNECT_DOC_ID
      );
      await setDoc(stripeConnectRef, STRIPE_TEACHER_DEFAULTS, { merge: true });

      // Mise à jour locale
      setTeachers((prev) => [...prev, { id: docRef.id, ...newTeacher }]);

      // Réinitialisation du formulaire
      setTeacherData({ firstName: "", lastName: "", email: "", phone: "", tutorType: "Teacher" });
      setSubjectIds([]);
      setSelectInputValue(""); // Réinitialiser la recherche
      setDisabledButton(false);

      // Afficher le Snackbar de succès
      setSnackbarMessage(t("Teacher added successfully"));
      setSnackbarSeverity("success");
      setSuccessSnackbarOpen(true);

      onClose();
    } catch (error) {
      console.error("Erreur lors de l'ajout du professeur :", error);
      setSnackbarMessage(t("An error occurred while adding the teacher"));
      setSnackbarSeverity("error");
      setErrorSnackbarOpen(true);
      setDisabledButton(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSuccessSnackbarOpen(false);
    setErrorSnackbarOpen(false);
  };

  return (
    <div>
      {/* Supprimez ou commentez cette ligne */}
      {/* <Typography sx={{color: 'red', display: 'flex', justifyContent: 'center'}}> {error} </Typography> */}
      
      <div>
        {/* Champ pour le prénom */}
        <TextField
          label={t("First name")}
          fullWidth
          margin="normal"
          value={teacherData.firstName}
          onChange={(e) => setTeacherData({ ...teacherData, firstName: e.target.value })}
        />

        {/* Champ pour le nom */}
        <TextField
          label={t("Last name")}
          fullWidth
          margin="normal"
          value={teacherData.lastName}
          onChange={(e) => setTeacherData({ ...teacherData, lastName: e.target.value })}
        />

        {/* Champ pour l'email */}
        <TextField
          label={t("Email")}
          fullWidth
          margin="normal"
          value={teacherData.email}
          onChange={(e) => setTeacherData({ ...teacherData, email: e.target.value })}
        />

        {/* Champ pour le téléphone */}
        <TextField
          label={t("Phone")}
          fullWidth
          margin="normal"
          value={teacherData.phone}
          onChange={(e) => setTeacherData({ ...teacherData, phone: e.target.value })}
        />

        {/* Tutor type */}
        <TextField
          select
          label={t("Tutor type")}
          fullWidth
          margin="normal"
          value={teacherData.tutorType}
          onChange={handleTutorTypeChange}
        >
          <MenuItem value="Teacher">{t("Teacher")}</MenuItem>
          <MenuItem value="Coach">{t("Coach")}</MenuItem>
        </TextField>

        {/* Dropdown animé pour les matières enseignées */}
        <Typography variant="subtitle1" style={{ marginTop: "1rem", fontSize: '18px' }}>
          {t("Subjects taught by the teacher")}
        </Typography>
        <Select
          isMulti
          closeMenuOnSelect={false}
          components={animatedComponents}
          options={subjectOptions}
          value={selectedSubjects}
          onChange={handleSubjectChange}
          onInputChange={handleInputChange}
          inputValue={selectInputValue}
          placeholder={t("Select one or more subjects")}
          styles={{
            menu: (provided) => ({ ...provided, zIndex: 9999 }) // Assurer que le menu est au-dessus
          }}
        />

        {/* Bouton pour soumettre */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleAddTeacher}
          sx={{ marginTop: "20px", fontSize: '15px' }}
          disabled={disabledButton}
        >
          {t("Add teacher")}
        </Button>
      </div>

      {/* Snackbar pour les messages de succès */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Snackbar pour les messages d'erreur */}
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default NewTeacherForm;
